import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  forceLogoutToLogin,
  isUnauthorizedApiResponse,
} from "../../utils/unauthorized";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || "/api";
const SUCCESS_CODES = new Set([200, 201, 204]);

const parseEnvelope = async (response) => {
  const isJson = response.headers
    .get("content-type")
    ?.toLowerCase()
    .includes("application/json");
  const payload = isJson ? await response.json() : null;

  if (isUnauthorizedApiResponse(response, payload)) {
    forceLogoutToLogin();
  }

  if (!payload || !SUCCESS_CODES.has(payload.resultCode)) {
    let message =
      payload?.resultDesc ||
      (response.ok ? "Unexpected response from server." : response.statusText);

    if (payload?.errors) {
      const errorMessages = Object.values(payload.errors)
        .flat()
        .filter(Boolean);
      if (errorMessages.length) {
        message = errorMessages.join(", ");
      }
    }

    const error = new Error(message);
    error.payload = payload;
    throw error;
  }

  if (payload.data !== undefined) {
    return payload.data;
  }

  return payload;
};

const getAuthHeaders = (state) => {
  const token = state.auth?.accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const buildQuery = (state) => {
  const { filters, pagination, sort } = state.sourceChannels;
  const params = new URLSearchParams();
  params.append("page", pagination.page + 1);
  params.append("pageSize", pagination.pageSize);
  if (filters.search.trim()) {
    params.append("search", filters.search.trim());
  }
  if (filters.status && filters.status !== "ALL") {
    params.append("status", filters.status);
  }
  if (sort.field) {
    params.append("sortBy", sort.field);
    params.append("sortDirection", sort.direction);
  }
  return params.toString();
};

export const fetchSourceChannels = createAsyncThunk(
  "sourceChannels/fetchList",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const query = buildQuery(state);
      const response = await fetch(`${API_BASE_URL}/source-channels?${query}`, {
        headers: {
          Accept: "application/json",
          ...getAuthHeaders(state),
        },
      });
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load source channels.");
    }
  }
);

export const fetchSourceChannelDetail = createAsyncThunk(
  "sourceChannels/fetchDetail",
  async (id, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(
        `${API_BASE_URL}/source-channels/${id}`,
        {
          headers: {
            Accept: "application/json",
            ...getAuthHeaders(state),
          },
        }
      );
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to load source channel.");
    }
  }
);

export const createSourceChannel = createAsyncThunk(
  "sourceChannels/create",
  async (payload, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(`${API_BASE_URL}/source-channels`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(state),
        },
        body: JSON.stringify(payload),
      });
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to create source channel."
      );
    }
  }
);

export const updateSourceChannel = createAsyncThunk(
  "sourceChannels/update",
  async ({ id, payload }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(`${API_BASE_URL}/source-channels/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...getAuthHeaders(state),
        },
        body: JSON.stringify(payload),
      });
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to update source channel."
      );
    }
  }
);

export const updateSourceChannelStatus = createAsyncThunk(
  "sourceChannels/updateStatus",
  async ({ id, status }, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(
        `${API_BASE_URL}/source-channels/${id}/status`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            ...getAuthHeaders(state),
          },
          body: JSON.stringify({ status }),
        }
      );
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(
        error.message || "Failed to update source channel status."
      );
    }
  }
);

export const generateSourceChannelSecret = createAsyncThunk(
  "sourceChannels/generateSecret",
  async (_, { getState, rejectWithValue }) => {
    try {
      const state = getState();
      const response = await fetch(
        `${API_BASE_URL}/source-channels/secret-key`,
        {
          method: "POST",
          headers: {
            Accept: "application/json",
            ...getAuthHeaders(state),
          },
        }
      );
      return await parseEnvelope(response);
    } catch (error) {
      return rejectWithValue(error.message || "Failed to generate secret key.");
    }
  }
);

const initialMeta = {
  page: 1,
  pageSize: 10,
  totalItems: 0,
  totalPages: 0,
  statusCounts: {
    ALL: 0,
    ACTIVE: 0,
    INACTIVE: 0,
  },
};

const initialState = {
  items: [],
  entities: {},
  meta: { ...initialMeta },
  filters: {
    status: "ALL",
    search: "",
  },
  sort: {
    field: "created_datetime",
    direction: "desc",
  },
  pagination: {
    page: 0,
    pageSize: 10,
  },
  loading: false,
  error: null,
  detail: {
    loading: false,
    error: null,
  },
  detailDialogId: null,
  formStatus: "idle",
  formError: null,
  statusUpdates: {},
  secretStatus: "idle",
  secretError: null,
};

const sourceChannelsSlice = createSlice({
  name: "sourceChannels",
  initialState,
  reducers: {
    setSourceSearch: (state, action) => {
      state.filters.search = action.payload || "";
      state.pagination.page = 0;
    },
    setSourceStatusFilter: (state, action) => {
      state.filters.status = action.payload || "ALL";
      state.pagination.page = 0;
    },
    setSourceSort: (state, action) => {
      const { field, direction } = action.payload || {};
      if (!field) return;
      state.sort = {
        field,
        direction: direction === "asc" ? "asc" : "desc",
      };
      state.pagination.page = 0;
    },
    setSourcePage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setSourcePageSize: (state, action) => {
      state.pagination.pageSize = action.payload;
      state.pagination.page = 0;
    },
    openSourceDetailDialog: (state, action) => {
      state.detailDialogId = action.payload;
    },
    closeSourceDetailDialog: (state) => {
      state.detailDialogId = null;
    },
    resetSourceForm: (state) => {
      state.formStatus = "idle";
      state.formError = null;
      state.secretStatus = "idle";
      state.secretError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchSourceChannels.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchSourceChannels.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        const items = payload.items || payload.data?.items || [];
        const meta = payload.meta || payload.data?.meta || {};
        state.items = items;
        state.entities = items.reduce((acc, item) => {
          if (item?.id != null) {
            acc[item.id] = item;
          }
          return acc;
        }, { ...state.entities });
        state.meta = {
          ...initialMeta,
          ...meta,
          statusCounts: {
            ALL: meta.statusCounts?.ALL ?? items.length,
            ACTIVE: meta.statusCounts?.ACTIVE ?? 0,
            INACTIVE: meta.statusCounts?.INACTIVE ?? 0,
          },
        };
        if (typeof state.meta.page === "number") {
          state.pagination.page = Math.max(state.meta.page - 1, 0);
        }
        if (typeof state.meta.pageSize === "number") {
          state.pagination.pageSize = state.meta.pageSize;
        }
      })
      .addCase(fetchSourceChannels.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to load source channels.";
      })
      .addCase(fetchSourceChannelDetail.pending, (state) => {
        state.detail.loading = true;
        state.detail.error = null;
      })
      .addCase(fetchSourceChannelDetail.fulfilled, (state, action) => {
        state.detail.loading = false;
        state.detail.error = null;
        const channel = action.payload;
        if (channel?.id != null) {
          state.entities[channel.id] = channel;
        }
      })
      .addCase(fetchSourceChannelDetail.rejected, (state, action) => {
        state.detail.loading = false;
        state.detail.error = action.payload || "Failed to load details.";
      })
      .addCase(createSourceChannel.pending, (state) => {
        state.formStatus = "loading";
        state.formError = null;
      })
      .addCase(createSourceChannel.fulfilled, (state) => {
        state.formStatus = "succeeded";
      })
      .addCase(createSourceChannel.rejected, (state, action) => {
        state.formStatus = "failed";
        state.formError = action.payload || "Failed to create source channel.";
      })
      .addCase(updateSourceChannel.pending, (state) => {
        state.formStatus = "loading";
        state.formError = null;
      })
      .addCase(updateSourceChannel.fulfilled, (state, action) => {
        state.formStatus = "succeeded";
        const channel = action.payload;
        if (channel?.id != null) {
          state.entities[channel.id] = channel;
          state.items = state.items.map((item) =>
            item.id === channel.id ? channel : item
          );
        }
      })
      .addCase(updateSourceChannel.rejected, (state, action) => {
        state.formStatus = "failed";
        state.formError = action.payload || "Failed to update source channel.";
      })
      .addCase(updateSourceChannelStatus.pending, (state, action) => {
        state.statusUpdates[action.meta.arg.id] = "loading";
      })
      .addCase(updateSourceChannelStatus.fulfilled, (state, action) => {
        const channel = action.payload;
        if (channel?.id != null) {
          state.entities[channel.id] = {
            ...(state.entities[channel.id] || {}),
            ...channel,
          };
          state.items = state.items.map((item) =>
            item.id === channel.id ? { ...item, ...channel } : item
          );
        }
        state.statusUpdates[action.meta.arg.id] = "idle";
      })
      .addCase(updateSourceChannelStatus.rejected, (state, action) => {
        state.statusUpdates[action.meta.arg.id] = "failed";
        state.error = action.payload || "Failed to update status.";
      })
      .addCase(generateSourceChannelSecret.pending, (state) => {
        state.secretStatus = "loading";
        state.secretError = null;
      })
      .addCase(generateSourceChannelSecret.fulfilled, (state) => {
        state.secretStatus = "succeeded";
      })
      .addCase(generateSourceChannelSecret.rejected, (state, action) => {
        state.secretStatus = "failed";
        state.secretError =
          action.payload || "Failed to generate secret key.";
      });
  },
});

export const {
  setSourceSearch,
  setSourceStatusFilter,
  setSourceSort,
  setSourcePage,
  setSourcePageSize,
  openSourceDetailDialog,
  closeSourceDetailDialog,
  resetSourceForm,
} = sourceChannelsSlice.actions;

export const selectSourceChannels = (state) => state.sourceChannels.items;
export const selectSourceChannelsMeta = (state) => state.sourceChannels.meta;
export const selectSourceChannelsFilters = (state) =>
  state.sourceChannels.filters;
export const selectSourceChannelsSort = (state) => state.sourceChannels.sort;
export const selectSourceChannelsPagination = (state) =>
  state.sourceChannels.pagination;
export const selectSourceChannelsLoading = (state) =>
  state.sourceChannels.loading;
export const selectSourceChannelsError = (state) => state.sourceChannels.error;
export const selectSourceChannelById = (state, id) =>
  state.sourceChannels.entities[id];
export const selectSourceChannelDetail = (state) =>
  state.sourceChannels.detail;
export const selectSourceDetailDialogId = (state) =>
  state.sourceChannels.detailDialogId;
export const selectSourceFormStatus = (state) =>
  state.sourceChannels.formStatus;
export const selectSourceFormError = (state) =>
  state.sourceChannels.formError;
export const selectSourceStatusUpdates = (state) =>
  state.sourceChannels.statusUpdates;
export const selectSecretStatus = (state) => state.sourceChannels.secretStatus;
export const selectSecretError = (state) => state.sourceChannels.secretError;

export default sourceChannelsSlice.reducer;
